import { useState, useEffect } from 'react'
import clsx from 'clsx'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useLazyQuery, gql, useReactiveVar } from '@apollo/client'
import { toast } from 'react-toastify'
import { Tooltip, Radio, Label } from 'flowbite-react'
import { v4 as uuidv4 } from 'uuid'

import { setAuthData } from '../hooks/useAuth'
import {
  apolloAuthData,
  setEditStatus,
  getEditStatus,
} from '../services/apollo'
import { ROLE_PARTICIPANT, ROLE_MANAGER } from '../constants'
import {
  serializeFromQuery,
  removeTypeName,
  normalizeCompletion,
} from '../utils'
import {
  PurifiedHTML,
  DialogEditKnowledgeCheck,
  DialogReorderKnowledgeChecks,
  ConfirmDialog,
} from '../components'

export const GET_USER_DATA = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      id
      capabilities
      completion
      email
      firstName
      lastName
      location
      points
      profilePic
    }
  }
`
const CREATE_COMPLETION = gql`
  mutation CreateCompletion(
    $postType: PostTypeEnum!
    $postId: String!
    $questionId: String!
    $id: String!
    $confidence: String!
  ) {
    createCompletion(
      input: {
        id: $id
        postType: $postType
        postId: $postId
        questionId: $questionId
        confidence: $confidence
      }
    ) {
      id
      postType
      postId
      questionId
      earned
      points
      completion
    }
  }
`

const KnowledgeCheck = ({
  databaseId,
  postType = '',
  postId = '',
  questions = [],
}) => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  let [searchParams, setSearchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')
  const [updatedKnowledgeChecks, setUpdatedKnowledgeChecks] = useState([])
  const knowledgeChecks =
    updatedKnowledgeChecks && updatedKnowledgeChecks.length > 0
      ? updatedKnowledgeChecks
      : questions
  const [doneCount, setDoneCount] = useState(-1)
  const [removingItemId, setRemovingItemId] = useState('')

  const [getUserData] = useLazyQuery(GET_USER_DATA, {
    fetchPolicy: 'no-cache',
  })

  /**
   * Always load latest user completion data: Step 1
   */
  useEffect(() => {
    if (!auth.user || doneCount > -1) {
      return
    }

    // Manager and facilitator users don't need to check for completion
    if (
      auth.user?.capabilities?.includes('manager') ||
      auth.user?.capabilities?.includes('facilitator')
    ) {
      setDoneCount(0)
      return
    }

    getUserData({ variables: { id: auth.user.id } }).then((user) => {
      // Save the completion count
      const completion = normalizeCompletion(user?.data?.user?.completion)
      setDoneCount(completion.filter((c) => c.p === postId).length)

      setAuthData({
        ...apolloAuthData(),
        user: {
          ...apolloAuthData().user,
          completion: completion ?? [],
        },
      })
    })
  }, [auth.user, doneCount, getUserData, postId])

  const handleAnswerDone = () => {
    setDoneCount((count) => ++count)
  }

  /**
   * Add a new question item below item at `index` position
   *
   * @param {[*]} items - An array of question items
   * @param {number} index - Index of item above which the new item to be added
   */
  const handleAddRow = (items, index) => {
    let newRows = Array.from(items)
    const newRowOrder = index + 1
    const newRow = generateDummyRow()

    // Add a new row with dummy data below the item at `index` position
    newRows.splice(newRowOrder, 0, newRow)

    // Add this to the editStatus
    const newChange = {
      page: postType,
      type: 'detailKnowledgeCheckQuestions',
      databaseId,
      atIndex: newRowOrder,
      data: newRow,
      action: 'create',
    }

    setEditStatus({
      ...getEditStatus(),
      changes: [...getEditStatus().changes, newChange],
    })

    // Save the updated modules
    setUpdatedKnowledgeChecks(newRows)
  }

  /**
   * Remove a row at `index`
   *
   * @param {number} index - Index of item to be removed
   */
  const handleRemoveRow = () => {
    if (removingItemId === '') {
      toast.error(`Unable to remove this question: Unknown item.`)
      return
    }

    // Clone the modules array
    const newRows = Array.from(knowledgeChecks)

    // Locate the item
    const itemIndex = newRows.findIndex(
      (newRow) => newRow.id === removingItemId
    )

    if (itemIndex === -1) {
      toast.error(`Unable to remove this question because it no longer exists.`)
      return
    }

    // Remove the module from the list
    newRows.splice(itemIndex, 1)

    // Add this to the editStatus
    const newChange = {
      databaseId,
      page: postType,
      type: 'detailKnowledgeCheckQuestions',
      atIndex: itemIndex,
      action: 'remove',
    }
    setEditStatus({
      ...getEditStatus(),
      changes: [...getEditStatus().changes, newChange],
    })

    // Save the updated modules
    setUpdatedKnowledgeChecks(newRows)

    // Close the confirm dialog
    setRemovingItemId('')
  }

  /**
   * Knowledge check editor confirm button
   *
   * @param {*} newData - New data
   */
  const handleEditorConfirm = (newData) => {
    if (!newData) {
      toast.error(
        `Unable to save knowledge check data: Incomplete question data.`
      )
      return
    }
    if (typeof newData.id !== 'string' || newData.id === '') {
      toast.error(`Unable to save knowledge check data: Unknown question ID.`)
      return
    }

    const newKnowledgeChecks = knowledgeChecks.map((kc) => {
      if (kc.id === newData.id) {
        return newData
      } else {
        return kc
      }
    })
    setUpdatedKnowledgeChecks(newKnowledgeChecks)

    // Generate a change for publishing
    const tagPage = postType
    const tagType = 'detailKnowledgeCheckQuestions'
    const newChange = {
      page: tagPage,
      type: tagType,
      databaseId,
      data: removeTypeName(newKnowledgeChecks),
      action: 'update',
    }
    const changeIndex = getEditStatus().changes.findIndex((change) => {
      return change.page === tagPage && change.type === tagType
    })
    let updatedChanges = Array.from(getEditStatus().changes)

    if (changeIndex > -1) {
      updatedChanges[changeIndex] = { ...newChange }
    } else {
      updatedChanges.push({ ...newChange })
    }

    setEditStatus({ ...getEditStatus(), changes: updatedChanges })

    // Close the editor dialog
    handleEditorCancel()
  }

  /**
   * Close the knowledge check editor
   */
  const handleEditorCancel = () => {
    const { kceditor, q, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }

  /**
   * Handle click event to open up the knowledge check reorder editor
   */
  const handleOpenReorderEditor = () => {
    setSearchParams({
      ...serializeFromQuery(searchParams),
      kcreorder: 'true',
    })
  }

  /**
   * Knowledge check reorder editor confirm button
   *
   * @param {[*]} updatedRowData - An array of updated knowledge checks
   */
  const handleReorderEditorConfirm = (updatedRowData = []) => {
    if (!updatedRowData) {
      toast.error(
        `Unable to update knowledge check data: Incomplete question data.`
      )
      return
    }

    setUpdatedKnowledgeChecks(updatedRowData)

    // Generate a change for publishing
    const tagPage = postType
    const tagType = 'detailKnowledgeCheckQuestions'
    const newChange = {
      page: tagPage,
      type: tagType,
      databaseId,
      data: removeTypeName(updatedRowData),
      action: 'update',
    }
    const changeIndex = getEditStatus().changes.findIndex((change) => {
      return change.page === tagPage && change.type === tagType
    })
    let updatedChanges = Array.from(getEditStatus().changes)

    if (changeIndex > -1) {
      updatedChanges[changeIndex] = { ...newChange }
    } else {
      updatedChanges.push({ ...newChange })
    }

    setEditStatus({ ...getEditStatus(), changes: updatedChanges })

    // Close the editor dialog
    handleReorderEditorCancel()
  }

  /**
   * Close the knowledge check reorder editor
   */
  const handleReorderEditorCancel = () => {
    const { kcreorder, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }

  if (!questions) {
    return null
  }

  return (
    <>
      <ConfirmDialog
        open={removingItemId !== ''}
        title="Remove Question"
        text="Are you sure you want to remove this question?"
        onCancel={() => setRemovingItemId('')}
        onConfirm={handleRemoveRow}
      />
      {/** Knowledge check editor dialog */}
      <DialogEditKnowledgeCheck
        open={
          searchParams.get('kceditor') === 'true' &&
          searchParams.get('q') !== ''
        }
        rowData={knowledgeChecks ?? []}
        // setRowData={setUpdatedKnowledgeChecks}
        title="Knowledge Check Editor"
        yesText="Save"
        noText="Cancel"
        onConfirm={handleEditorConfirm}
        onCancel={handleEditorCancel}
      />
      {/** Knowledge check reorder dialog */}
      <DialogReorderKnowledgeChecks
        open={searchParams.get('kcreorder') === 'true'}
        rowData={knowledgeChecks ?? []}
        setRowData={setUpdatedKnowledgeChecks}
        title="Reorder Questions"
        yesText="Save"
        noText="Cancel"
        onConfirm={handleReorderEditorConfirm}
        onCancel={handleReorderEditorCancel}
      />

      {/* {questions.map((question, dataIndex) => { */}
      {knowledgeChecks.map((question, dataIndex) => {
        return (
          <div className="relative" key={`${postId}-${question.id}`}>
            {isEditOn && (
              <div className="absolute -right-[calc(4*14px)] top-1/2 flex -translate-y-1/2 justify-between space-x-1">
                <button
                  onClick={() => handleAddRow(knowledgeChecks, dataIndex)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={3}
                    stroke="currentColor"
                    className="h-[14px] w-[14px] stroke-green"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4.5v15m7.5-7.5h-15"
                    />
                  </svg>
                </button>
                <button
                  className="disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={dataIndex === 0}
                  onClick={() =>
                    dataIndex > 0 ? setRemovingItemId(question.id) : null
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-[14px] w-[14px] stroke-red-500"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
                <button
                  className="disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={knowledgeChecks.length < 2}
                  onClick={
                    knowledgeChecks.length > 1 ? handleOpenReorderEditor : null
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="h-[14px] w-[14px]"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"
                    />
                  </svg>
                </button>
              </div>
            )}
            <KnowledgeCheckQuestion
              postType={postType}
              postId={postId}
              question={question}
              dataIndex={dataIndex}
              doneCount={doneCount}
              handleAnswerDone={handleAnswerDone}
              // total={questions.length}
              total={knowledgeChecks.length}
            />
          </div>
        )
      })}
    </>
  )
}

const KnowledgeCheckQuestion = ({
  postType,
  postId,
  doneCount,
  dataIndex,
  total,
  question: { id: questionId, statement, choices, feedback },
  handleAnswerDone,
}) => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  const [createCompletion] = useMutation(CREATE_COMPLETION)
  const [earnedPoint, setEarnedPoint] = useState(0)
  const [value, setValue] = useState('')
  const [answered, setAnswered] = useState(false)
  const [wasDone, setWasDone] = useState(false)
  const [selectedConfidence, setSelectedConfidence] = useState('')
  const [submitting, setSubmitting] = useState(false)
  let [searchParams, setSearchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')

  useEffect(() => {
    if (answered || !auth.user) {
      return
    }

    const completion = auth.user.completion
      ? normalizeCompletion(auth.user.completion).find(
          (c) => c.p === postId && c.q === questionId
        )
      : null

    if (completion) {
      setAnswered(true)
      const choice = choices.find((choice) => choice.id === completion.i)
      if (choice) {
        setValue(choice.id)
      }
      setSelectedConfidence(completion.c)
      setWasDone(true)
    }
  }, [auth.user, choices, postId, questionId, answered])

  const getChoice = (opts, selectedOpt) => {
    return opts.find((opt) => opt.id === selectedOpt)
  }

  const handleClick = (e) => {
    setSelectedConfidence(e.target.id)
  }

  const handleChange = (e) => {
    setValue(e.target.id)
  }

  const handleSubmit = async () => {
    const choice = getChoice(choices, value)

    // Set submitting status
    setSubmitting(true)

    // Stop early if there's no auth.user
    if (!auth.user) {
      toast.warning(
        `Your answer wasn't saved. Your session might be expired. Please log in again to continue.`
      )
      return
    }

    // Only save the progress if the user's role is not either "Manager" or "Facilitator"
    if (
      !auth.user?.capabilities?.includes('manager') &&
      !auth.user?.capabilities?.includes('facilitator')
    ) {
      try {
        const result = await createCompletion({
          variables: {
            postType,
            postId,
            questionId,
            id: choice.id,
            confidence: selectedConfidence,
          },
        })
        const { earned, points, completion } = result?.data?.createCompletion
        setAuthData({
          ...apolloAuthData(),
          user: { ...auth.user, points, completion },
        })

        // Save earned points
        setEarnedPoint(earned)

        // Mark as answered
        setAnswered(true)

        // Increment the answerDone counter
        handleAnswerDone()
      } catch (err) {
        console.log('markAsComplete :: err', err)
        if (err.message.includes('Answer already exists')) {
          toast.error(
            `You already answered this question in another window! Your points have been recorded and you should now see a completion check on the ${
              postType === 'AGENDA_ITEM'
                ? 'agenda'
                : postType === 'LEARNING_LOUNGE_ITEM'
                ? 'learning lounge'
                : ''
            } page.`,
            {
              autoClose: false,
            }
          )
        } else if (
          err.message.includes("This post doesn't have any knowledge checks")
        ) {
          toast.error(
            `This page doesn't have any knowledge check questions. Please make sure you are accessing the right page.`,
            {
              autoClose: false,
            }
          )
        } else {
          toast.error(
            `Unable to save your answer. Please refresh your page and try again!`,
            {
              autoClose: false,
            }
          )
        }
      } finally {
        // Reset submitting status
        setSubmitting(false)
      }
    } else {
      // Mark as answered
      setAnswered(true)

      // Increment the answerDone counter
      handleAnswerDone()

      // Reset submitting status
      setSubmitting(false)
    }
  }

  /**
   * Handle click event to open up the knowledge check editor
   */
  const handleOpenEditor = () => {
    setSearchParams({
      ...serializeFromQuery(searchParams),
      kceditor: 'true',
      q: questionId,
    })
  }

  const choice = choices.find((choice) => choice.id === value)

  return (
    <>
      <div
        role={isEditOn ? 'button' : 'none'}
        className={clsx(
          'mb-10 rounded-[9px] bg-bright-gray px-5 py-4',
          isEditOn ? 'visible' : dataIndex <= doneCount ? 'visible' : 'hidden',
          isEditOn &&
            'cursor-pointer select-none outline outline-0 hover:bg-beer/25 hover:outline-1 hover:outline-beer active:translate-y-[0.75px] active:scale-[0.99]'
        )}
        onClick={isEditOn ? handleOpenEditor : () => null}
      >
        <div className="mb-5 text-xl font-bold text-flame">
          Question {dataIndex + 1} of {total}
        </div>
        {/** Question statement */}
        <PurifiedHTML dom="div" html={statement} className="font-medium" />

        {/** Question choices */}
        <div
          className={clsx(
            'ml-4 mt-4 [&>div:not(:last-of-type)]:mb-4',
            isEditOn && 'pointer-events-none'
          )}
        >
          {choices.map((o) => {
            return (
              <div className="mb-4 flex items-center gap-4" key={o.id}>
                <Radio
                  className="h-4 w-4 border border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:bg-blue-600 dark:focus:ring-blue-600"
                  id={o.id}
                  name={o.id}
                  value={o.text}
                  checked={value === o.id}
                  disabled={answered}
                  onChange={handleChange}
                />
                <Label
                  htmlFor={o.id}
                  className={clsx(
                    'font-normal text-dark-charcoal dark:text-gray-300',
                    answered && 'opacity-60'
                  )}
                >
                  {o.text}
                </Label>
              </div>
            )
          })}
        </div>

        {/** Confidence buttons */}
        {!isEditOn && (
          <div className="relative">
            {auth.user?.capabilities?.includes(ROLE_PARTICIPANT) && (
              <div
                className={clsx(
                  'invisible absolute bottom-0 left-0 z-[1] w-full text-center font-bold text-flame opacity-0',
                  answered && !wasDone && 'animate-fadeUp'
                )}
              >
                You've earned {`${earnedPoint}`} points
              </div>
            )}
            <ConfidenceButtons
              choiceSelected={value !== ''}
              selectedConfidence={selectedConfidence}
              answered={answered}
              onClick={handleClick}
            />
          </div>
        )}

        {/** Submit button */}
        {!isEditOn && !answered && (
          <div className="text-center">
            <button
              type="button"
              className={clsx(
                'rounded-full bg-skin-button-accent px-4 py-3 leading-none text-white transition-colors',
                selectedConfidence !== '' && value !== ''
                  ? 'cursor-pointer hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]'
                  : 'cursor-not-allowed opacity-50'
              )}
              disabled={selectedConfidence === '' || value === '' || submitting}
              onClick={handleSubmit}
            >
              submit
            </button>
          </div>
        )}

        {/** Message to the manager and facilitator */}
        {!isEditOn &&
          answered &&
          auth.user?.capabilities?.includes('manager') && (
            <p className="text-center text-xs font-bold">
              Sorry! Administrators can't earn leaderboard points
            </p>
          )}
        {!isEditOn &&
          answered &&
          auth.user?.capabilities?.includes('facilitator') && (
            <p className="text-center text-xs font-bold">
              Sorry! Facilitators can't earn leaderboard points
            </p>
          )}

        {/** Feedback */}
        {!isEditOn && answered && (
          <PurifiedHTML
            dom="div"
            html={
              choice && choice?.correct ? feedback.correct : feedback.incorrect
            }
            className="mt-6 rounded-[6px] bg-light-gray px-5 py-4 shadow-sm"
          />
        )}
      </div>

      {/** Completed Text  */}
      {!isEditOn && doneCount === total && dataIndex + 1 === total && (
        <div className="mb-5 text-center text-xl font-bold text-flame">
          Congratulations! You’ve completed this Reflection Point!
        </div>
      )}
    </>
  )
}

function ConfidenceButtons({
  choiceSelected = false,
  selectedConfidence,
  answered = false,
  onClick = () => {},
}) {
  return (
    <div className={clsx('group my-8', answered && 'answered')}>
      <div className="flex items-center gap-1 text-lg font-bold">
        <div className="group-[.answered]:opacity-50">
          How confident are you in your answer?
        </div>
        <Tooltip
          style="light"
          content={
            <div className="text-xs font-normal">
              <p>
                Confidence is an important trait, but it's equally important to
                be honest with yourself. If you are truly confident in an answer
                - and you are correct - a high confidence will result in the
                highest possible points.
              </p>
              <p>
                But... being confident when you actually don't know the answer
                can impact your perceived credibility in the business world. So,
                if you answer incorrectly but are highly confident, you'll earn
                the lowest number of points possible.
              </p>
              <p>Think through your choices carefully!</p>
            </div>
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-6 w-6"
          >
            <path
              fillRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z"
              clipRule="evenodd"
            />
          </svg>
        </Tooltip>
      </div>
      <div className="mt-4 flex justify-between group-[.answered]:opacity-50">
        {[
          { text: 'Very confident', id: 'very' },
          { text: 'Somewhat confident', id: 'somewhat' },
          { text: 'Not at all confident', id: 'not' },
        ].map((btn) => (
          <button
            key={btn.text}
            type="button"
            className={clsx(
              'transition-color rounded-full border-2 border-skin-fill px-4 py-3 leading-none',
              answered || !choiceSelected
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer hover:bg-skin-fill hover:text-white active:translate-y-[0.75px] active:scale-[0.99]',
              selectedConfidence === btn.id ? 'bg-skin-fill text-white' : ''
            )}
            id={btn.id}
            disabled={answered || !choiceSelected}
            onClick={onClick}
          >
            {btn.text}
          </button>
        ))}
      </div>
    </div>
  )
}

const generateDummyRow = () => {
  const dummyRow = {
    id: uuidv4(),
    statement: '<p></p>',
    choices: [
      {
        id: uuidv4(),
        correct: true,
        text: 'Choice 1',
      },
      {
        id: uuidv4(),
        correct: false,
        text: 'Choice 2',
      },
      {
        id: uuidv4(),
        correct: false,
        text: 'Choice 3',
      },
      {
        id: uuidv4(),
        correct: false,
        text: 'Choice 4',
      },
    ],
    point: 120,
    gradingMethod: 'GRADED_WITH_CONFIDENCE_SCORE',
    feedback: {
      correct: '<p></p>',
      incorrect: '<p></p>',
    },
  }

  return dummyRow
}

export default KnowledgeCheck
